import React, { useState, useEffect } from 'react'
import { Table, Button, Input} from 'reactstrap'
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents'
import { addData, updateData, getStudentIdData as getStudentsGradesInMonths } from '../../lib/gradeinmonths'
import { getDataWithStudent as getLessonsFromCourseLesson } from '../../lib/courselesson'
import { getIdData as getStudentInfo } from '../../lib/studentinfo'
import { ClassForLessonSelect } from '../Common/FormSelects'
import { GPA_GRADES_MONTH_ORDER, GPA_GRADES_PERIOD_ORDER, gradePeriodSortKey } from '../../utils/gradeUtils'

const MONTH_LIST = GPA_GRADES_MONTH_ORDER
const PERIOD_OPTIONS = GPA_GRADES_PERIOD_ORDER

/** @deprecated use gradePeriodSortKey from gradeUtils — kept as alias for local sortColumns */
const columnSortKey = gradePeriodSortKey

function sortColumns(a, b) {
	return columnSortKey(a) - columnSortKey(b)
}

function columnHeaderLabel(col) {
	if (MONTH_LIST.includes(col)) return col.substring(0, 3)
	const map = {
		'Quarter 1': 'Q1',
		'Quarter 2': 'Q2',
		'Quarter 3': 'Q3',
		'Quarter 4': 'Q4',
		'Semester 1': 'S1',
		'Semester 2': 'S2'
	}
	return map[col] || col
}

const GradeInMonth2 = ({ studentId, classId, isStudent, isProfile = false }) => {
	const [sClass, setSClass] = useState(null)
	const [lessons, setLessons] = useState([])
	const [gradeRecords, setGradeRecords] = useState([])
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)
	const [loading, setLoading] = useState(true)
	const [submitting, setSubmitting] = useState(false)
	const [selectedPeriods, setSelectedPeriods] = useState([])
	const [periodToAdd, setPeriodToAdd] = useState('')
	/** Bumps after each load so uncontrolled grade inputs remount with correct defaultValue */
	const [gradesFormKey, setGradesFormKey] = useState(0)

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	async function fetchData() {
		try {
			setLoading(true)

			// Determine the class to use
			let effectiveClass = sClass || classId

			// If we still don't have a class, fetch student data
			if (!effectiveClass) {
				const studentData = await getStudentInfo(studentId)
				if (studentData && studentData.length > 0) {
					effectiveClass = studentData[0].class || 9
					setSClass(effectiveClass)
				} else {
					effectiveClass = 9 // Default fallback
					setSClass(effectiveClass)
				}
			}

			// Get grades from standard GPA-Grades (gradeinmonths table)
			const gradesData = await getStudentsGradesInMonths(studentId)
			setGradeRecords(gradesData)

			// Get lessons from student-lessons (courselesson table)
			const lessonsData = await getLessonsFromCourseLesson(studentId)
			const classNum = Number(effectiveClass)
			// Only courses for the selected / current grade year (courseyear 9–12), not prior years
			const lessonsForCurrentYear = lessonsData.filter((l) => Number(l.courseyear) === classNum)
			setLessons(lessonsForCurrentYear)

			setGradesFormKey((k) => k + 1)

			// Extract months that already have grades for these lessons
			const lessonIds = lessonsForCurrentYear.map((l) => l.id)
			const autoSelectedPeriods = [
				...new Set(
					gradesData
						.filter((g) => lessonIds.includes(g.lesson) && g.grade > 0)
						.map((g) => g.month)
				)
			].sort(sortColumns)

			if (autoSelectedPeriods.length > 0) {
				setSelectedPeriods((prev) => {
					const combined = [...new Set([...prev, ...autoSelectedPeriods])]
					return combined.sort(sortColumns)
				})
			}
		} catch (error) {
			console.error('Error fetching data:', error)
		} finally {
			setLoading(false)
		}
	}

	function getGrade(lesId, month) {
		const matchingRecord = gradeRecords.find(r => r.lesson === lesId && r.month === month)
		return matchingRecord ? matchingRecord.grade : 0
	}

	/** Stored grade for this course + period only. Do not copy one month's grade into other period columns (breaks partial updates). */
	function displayGradeForCell(less, period) {
		const rec = gradeRecords.find((r) => r.lesson === less.id && r.month === period)
		if (rec) return rec.grade
		if (less.autograde) return ''
		return getGrade(less.id, period)
	}

	function getRecordId(lessonId, month) {
		const matchingRecord = gradeRecords.find(r => r.lesson === lessonId && r.month === month)
		return matchingRecord ? matchingRecord.id : 0
	}

	function getGradeColor(gradeValue) {
		let color = '#FFFFFF'
		if (gradeValue > 0 && gradeValue < 65) color = '#FF0000'
		if (gradeValue > 64 && gradeValue < 71) color = '#FFA500'
		if (gradeValue > 70 && gradeValue < 81) color = '#FFFF00'
		if (gradeValue > 80 && gradeValue < 91) color = '#32CD32'
		if (gradeValue > 90) color = '#1E90FF'

		return { backgroundColor: color }
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setSubmitting(true)

		try {
			const form = e.target
			const formData = new FormData(form)
			const formEntries = [...formData.entries()]

			const updatePromises = formEntries
				.filter(([name, value]) => {
					const trimmed = value === undefined || value === null ? '' : String(value).trim()
					if (trimmed === '') return false
					const lastDash = name.lastIndexOf('-')
					if (lastDash <= 0) return false
					const month = name.slice(0, lastDash)
					const lessonId = parseInt(name.slice(lastDash + 1), 10)
					if (!month || Number.isNaN(lessonId)) return false
					const gradeVal = parseInt(trimmed, 10)
					if (Number.isNaN(gradeVal)) return false
					const existing = gradeRecords.find((r) => r.lesson === lessonId && r.month === month)
					// Only persist when creating a new row or the grade actually changed
					if (existing && existing.grade === gradeVal) return false
					return true
				})
				.map((entry) => {
					const [name, value] = entry
					const lastDash = name.lastIndexOf('-')
					const month = name.slice(0, lastDash)
					const lessonId = parseInt(name.slice(lastDash + 1), 10)
					const recId = getRecordId(lessonId, month)
					const gradeVal = parseInt(String(value).trim(), 10)

					if (recId > 0) {
						return updateData({ id: recId, grade: gradeVal })
					} else {
						return addData({
							studentId,
							month,
							lesson: lessonId,
							grade: gradeVal
						})
					}
				})

			if (updatePromises.length > 0) {
				await Promise.all(updatePromises)
			}

			showToastMessage('Grades updated successfully!')
			fetchData()
		} catch (error) {
			console.error('Error updating grades:', error)
		} finally {
			setSubmitting(false)
		}
	}

	const handleAddPeriod = () => {
		if (periodToAdd && !selectedPeriods.includes(periodToAdd)) {
			setSelectedPeriods([...selectedPeriods, periodToAdd].sort(sortColumns))
			setPeriodToAdd('')
		}
	}

	useEffect(() => {
		if (studentId) {
			fetchData()
		}
	}, [studentId, sClass])

	return (
		<div className="card mb-4 shadow-sm" style={{ borderRadius: '8px' }}>
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
			<div className="card-header border-bottom d-flex justify-content-between align-items-center">
				<h5 className="mb-0">Update Grades (Rigor-Courses)</h5>
			</div>
			<div className="card-body">
				<div className="d-flex pb-3 justify-content-between align-items-center flex-wrap gap-3">
					<div className="d-flex align-items-center gap-3">
						<div className="d-flex align-items-center">
							<h6 className="mb-0 me-3">Class:</h6>
							{isStudent ? (
								<span className="fw-bold">{classId}</span>
							) : (
								<div style={{ minWidth: '150px' }}>
									<ClassForLessonSelect value={sClass} setValue={setSClass} />
								</div>
							)}
						</div>
					</div>
					
					<div className="d-flex align-items-center gap-2">
						<div style={{ minWidth: '200px' }}>
							<Input
								type="select"
								value={periodToAdd}
								onChange={(e) => setPeriodToAdd(e.target.value)}
								className="form-select-sm"
							>
								<option value="">Select Month / Quarter / Semester</option>
								<optgroup label="Months">
									{MONTH_LIST.filter((m) => !selectedPeriods.includes(m)).map((m) => (
										<option key={m} value={m}>{m}</option>
									))}
								</optgroup>
								<optgroup label="Quarters & Semesters">
									{PERIOD_OPTIONS.filter((p) => !selectedPeriods.includes(p)).map((p) => (
										<option key={p} value={p}>{p}</option>
									))}
								</optgroup>
							</Input>
						</div>
						<Button color="success" size="sm" onClick={handleAddPeriod} disabled={!periodToAdd}>
							<i className="fa fa-plus me-1"></i> Add
						</Button>
					</div>
				</div>

				{loading ? (
					<div className="text-center">
						<div className="spinner-border" role="status">
							<span className="visually-hidden">Loading...</span>
						</div>
						<p className="mt-2">Loading...</p>
					</div>
				) : (
					<form onSubmit={handleSubmit}>
						<div className="table-responsive">
							<Table hover bordered className="mb-0">
								<thead>
									<tr>
										<th style={{ width: '250px' }}>Course (from Rigor-Courses)</th>
										{selectedPeriods.map((period) => (
											<th key={period} className="text-center" style={{ minWidth: '100px' }}>
												{columnHeaderLabel(period)}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{lessons.length > 0 ? (
										lessons.map(less => (
											<tr key={less.id}>
												<td className="fw-bold">
													{less.coursename}
													<div className="text-muted small">
														{less.department} - {less.level}
														{less.autograde && <span className="badge bg-soft-info text-primary ms-2">Auto Grade</span>}
													</div>
												</td>
												{selectedPeriods.map((period) => {
													const currentGrade = displayGradeForCell(less, period)
													const colorVal =
														currentGrade === '' || currentGrade === '-'
															? 0
															: Number(currentGrade)
													return (
														<td key={period} className="p-1">
															<Input
																key={`${gradesFormKey}-${less.id}-${period}`}
																type="number"
																name={`${period}-${less.id}`}
																defaultValue={currentGrade === '-' || currentGrade === '' ? '' : currentGrade}
																className="form-control-sm text-center"
																style={getGradeColor(colorVal)}
																placeholder="-"
																min="0"
																max="100"
															/>
														</td>
													)
												})}
											</tr>
										))
									) : (
										<tr>
											<td colSpan={selectedPeriods.length + 1} className="text-center">
												No courses for Grade {sClass ?? classId ?? '—'} in Rigor-Courses. Add or edit courses in List of Courses
												and set Course Year to this grade.
											</td>
										</tr>
									)}
								</tbody>
							</Table>
						</div>
						{selectedPeriods.length > 0 && (
							<div className="d-flex justify-content-end mt-3">
								<Button type="submit" color="primary" disabled={submitting}>
									<i className="fa fa-save me-2"></i>
									{submitting ? 'Updating...' : 'Update Grades'}
								</Button>
							</div>
						)}
					</form>
				)}
			</div>
		</div>
	)
}

export { GradeInMonth2 }
